import React, { useState, useEffect } from 'react';
import {
  FileText,
  DollarSign,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Eye,
  Send,
  Search,
  Receipt,
  FileCheck,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { getAllInvoices, getInvoiceStats } from '../../api/wholesale/invoices';
import { getPayments } from '../../api/wholesale/payments';
import { getOrders } from '../../api/wholesale/orders';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import InvoicePreviewModal from '../../components/wholesale/InvoicePreviewModal';
import ReceiptPreviewModal from '../../components/wholesale/ReceiptPreviewModal';
import ProformaInvoicePreviewModal from '../../components/wholesale/ProformaInvoicePreviewModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const InvoicesPage = () => {
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all'); // 'all', 'invoice', 'proforma', 'receipt'
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal states
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showProformaModal, setShowProformaModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateParams = getDateRangeParams();
      const params = { ...dateParams };
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Fetch all document types in parallel
      const [invoicesRes, paymentsRes, ordersRes] = await Promise.all([
        getAllInvoices({ ...params, limit: 1000 }).catch(() => ({ data: [] })),
        getPayments({ ...params, limit: 1000 }).catch(() => ({ data: [] })),
        getOrders({ ...params, limit: 1000 }).catch(() => ({ data: [] }))
      ]);

      // Normalize invoices
      const invoices = (invoicesRes?.data || invoicesRes || []).map((inv) => ({
        ...inv,
        documentType: 'invoice',
        documentNumber: inv.invoice_number,
        documentDate: inv.invoice_date || inv.created_at,
        customerName: inv.distributor_name,
        amount: parseFloat(inv.total_amount) || 0,
        status: inv.status,
        originalData: inv
      }));

      // Normalize receipts (from payments)
      const receipts = (paymentsRes?.data || []).map((pay) => ({
        ...pay,
        documentType: pay.order_id ? 'receipt' : 'standalone_receipt',
        documentNumber: pay.payment_number || `REC-${pay.id}`,
        documentDate: pay.payment_date || pay.created_at,
        customerName: pay.distributor_business_name,
        amount: parseFloat(pay.payment_amount) || 0,
        status: pay.payment_status || 'confirmed',
        originalData: pay
      }));

      // Normalize proforma invoices (from orders that have proforma_invoice_number)
      const proformas = (ordersRes?.data || [])
        .filter((order) => order.proforma_invoice_number)
        .map((order) => ({
          ...order,
          documentType: 'proforma',
          documentNumber: order.proforma_invoice_number,
          documentDate: order.proforma_generated_at || order.created_at,
          customerName: order.distributor_business_name,
          amount: parseFloat(order.total_amount) || 0,
          status: order.order_status,
          originalData: order
        }));

      // Combine all documents
      let allDocs = [...invoices, ...receipts, ...proformas];

      // Filter by document type
      if (documentTypeFilter !== 'all') {
        allDocs = allDocs.filter((doc) => doc.documentType === documentTypeFilter);
      }

      // Filter by status (for invoices mainly)
      if (statusFilter !== 'all') {
        allDocs = allDocs.filter((doc) => doc.status === statusFilter);
      }

      // Sort documents
      allDocs.sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'date':
            comparison = new Date(a.documentDate) - new Date(b.documentDate);
            break;
          case 'number':
            comparison = (a.documentNumber || '').localeCompare(b.documentNumber || '');
            break;
          case 'customer':
            comparison = (a.customerName || '').localeCompare(b.customerName || '');
            break;
          case 'amount':
            comparison = a.amount - b.amount;
            break;
          case 'type':
            comparison = a.documentType.localeCompare(b.documentType);
            break;
          default:
            comparison = new Date(a.documentDate) - new Date(b.documentDate);
        }
        return sortDirection === 'desc' ? -comparison : comparison;
      });

      setDocuments(allDocs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const dateParams = getDateRangeParams();
      const response = await getInvoiceStats(dateParams);
      setStats(response?.data || response);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [documentTypeFilter, statusFilter, periodRange]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDocuments();
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchQuery, sortField, sortDirection]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [documentTypeFilter, statusFilter, searchQuery]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDocuments = documents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(documents.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRowClick = (doc) => {
    setSelectedDocument(doc);
    if (doc.documentType === 'invoice') {
      setShowInvoiceModal(true);
    } else if (doc.documentType === 'receipt' || doc.documentType === 'standalone_receipt') {
      setShowReceiptModal(true);
    } else if (doc.documentType === 'proforma') {
      setShowProformaModal(true);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-300" />;
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-emerald-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-emerald-600" />
    );
  };

  const documentTypeTabs = [
    { id: 'all', label: 'All Documents', icon: FileText },
    { id: 'invoice', label: 'Invoices', icon: FileText },
    { id: 'proforma', label: 'Proforma', icon: FileCheck },
    { id: 'receipt', label: 'Order Receipts', icon: Receipt },
    { id: 'standalone_receipt', label: 'Standalone', icon: FileText }
  ];

  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
    viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700' },
    partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700' },
    paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
    overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
    confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700' },
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700' }
  };

  const documentTypeStyles = {
    invoice: { label: 'Invoice', color: 'bg-blue-100 text-blue-700', icon: FileText },
    proforma: { label: 'Proforma', color: 'bg-purple-100 text-purple-700', icon: FileCheck },
    receipt: { label: 'Order Receipt', color: 'bg-emerald-100 text-emerald-700', icon: Receipt },
    standalone_receipt: {
      label: 'Standalone',
      color: 'bg-orange-100 text-orange-700',
      icon: FileText
    }
  };

  if (loading && documents.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">View and manage all wholesale documents</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Total Invoices</p>
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_invoices || 0}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Total Amount</p>
              <DollarSign className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ₦{parseFloat(stats.total_amount || 0).toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Outstanding</p>
              <TrendingUp className="h-5 w-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ₦{parseFloat(stats.total_outstanding || 0).toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">
              ₦{parseFloat(stats.overdue_amount || 0).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">{stats.overdue_invoices || 0} invoices</p>
          </div>
        </div>
      )}

      {/* Document Type Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {documentTypeTabs.map((tab) => {
            const Icon = tab.icon;
            const count = documents.filter(
              (d) => tab.id === 'all' || d.documentType === tab.id
            ).length;
            return (
              <button
                key={tab.id}
                onClick={() => setDocumentTypeFilter(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2
                  ${
                    documentTypeFilter === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span
                  className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    documentTypeFilter === tab.id
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by document number, customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          {documentTypeFilter === 'invoice' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Type
                    <SortIcon field="type" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('number')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Document No
                    <SortIcon field="number" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('date')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Date & Time
                    <SortIcon field="date" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('customer')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Customer
                    <SortIcon field="customer" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('amount')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Amount
                    <SortIcon field="amount" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No documents found
                  </td>
                </tr>
              ) : (
                currentDocuments.map((doc, index) => {
                  const typeStyle =
                    documentTypeStyles[doc.documentType] || documentTypeStyles.invoice;
                  const TypeIcon = typeStyle.icon;
                  const status = statusConfig[doc.status] || statusConfig.sent;

                  return (
                    <tr
                      key={`${doc.documentType}-${doc.id || index}`}
                      onClick={() => handleRowClick(doc)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeStyle.color}`}
                        >
                          <TypeIcon className="w-3.5 h-3.5" />
                          {typeStyle.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-emerald-600">
                          {doc.documentNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="text-gray-900 font-medium">
                          {new Date(doc.documentDate).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(doc.documentDate).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {doc.customerName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₦{doc.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}
                        >
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {documents.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow-sm">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-medium">{Math.min(indexOfLastItem, documents.length)}</span>{' '}
                of <span className="font-medium">{documents.length}</span> results
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ArrowUpDown className="h-5 w-5 rotate-90" aria-hidden="true" />
                </button>

                {/* Simplified page numbers - just current/total if many pages, or all if few */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Logic to show a window of pages around current
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        currentPage === pageNum
                          ? 'zIndex-10 bg-emerald-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ArrowUpDown className="h-5 w-5 -rotate-90" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoiceModal && selectedDocument && (
        <InvoicePreviewModal
          order={{
            id: selectedDocument.originalData?.order_id,
            order_number: selectedDocument.originalData?.order_number,
            distributor_business_name: selectedDocument.customerName,
            distributor_phone: selectedDocument.originalData?.distributor_phone,
            invoice_number: selectedDocument.documentNumber,
            total_amount: selectedDocument.originalData?.total_amount || selectedDocument.amount
          }}
          invoice={selectedDocument.originalData}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedDocument(null);
          }}
          onSuccess={() => fetchDocuments()}
        />
      )}

      {/* Receipt Preview Modal */}
      {showReceiptModal && selectedDocument && (
        <ReceiptPreviewModal
          receipt={{
            ...selectedDocument.originalData,
            receipt_number: selectedDocument.documentNumber
          }}
          order={{
            id: selectedDocument.originalData?.order_id,
            order_number: selectedDocument.originalData?.order_number,
            distributor_business_name: selectedDocument.customerName,
            distributor_phone: selectedDocument.originalData?.distributor_phone
          }}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* Proforma Preview Modal */}
      {showProformaModal && selectedDocument && (
        <ProformaInvoicePreviewModal
          order={selectedDocument.originalData}
          onClose={() => {
            setShowProformaModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default InvoicesPage;
